import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Form,
  Select,
  InputNumber,
  Button,
  Space,
  Divider,
  Row,
  Col,
  Alert,
  Spin,
  Typography,
  Statistic,
  Input,
  List,
  Popconfirm,
  message,
  Collapse
} from 'antd';
import {
  CalculatorOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  DollarOutlined,
  CaretRightOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useGetProducts } from '@/hooks/customer/useCustomer';
import { useCalculateLoanFees, useGenerateLoanScheduleFromConfig } from '@/shared/api/client/loan-schedule';
import { Deduction, LoanImpactAnalysis } from './types';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface LoanCalculatorCardProps {
  className?: string;
  collapsedPlaceholderMinHeight?: number;
}

export const LoanCalculatorCard: React.FC<LoanCalculatorCardProps> = ({
  className,
  collapsedPlaceholderMinHeight = 170,
}) => {
  const [form] = Form.useForm();
  const { dark } = useTheme();
  const authUser = useAuthUser();
  const { branch, custNo, fullname } = authUser;

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [selectedProductGlNo, setSelectedProductGlNo] = useState<string | null>(null);
  const [loanAmount, setLoanAmount] = useState<number>(0);
  const [loanPeriod, setLoanPeriod] = useState<number>(12);
  const [grossSalary, setGrossSalary] = useState<number>(0);
  const [netSalary, setNetSalary] = useState<number>(0);
  const [deductions, setDeductions] = useState<Deduction[]>([]);
  const [newDeductionName, setNewDeductionName] = useState('');
  const [newDeductionAmount, setNewDeductionAmount] = useState<number>(0);

  const [productsQuery] = useGetProducts(authUser, 'L');
  const { data: products, isLoading: isProductsLoading } = productsQuery;

  const selectedProduct = useMemo(() => {
    return products?.find(p => p.glNo === selectedProductGlNo);
  }, [products, selectedProductGlNo]);

  const { data: feeCalculationResponse, isFetching: isLoadingFees } = useCalculateLoanFees(
    { 
      glNo: selectedProductGlNo || '', 
      loanAmount: loanAmount,
      loanPeriod: loanPeriod
    },
    {
      query: {
        enabled: !!selectedProductGlNo && loanAmount > 0 && loanPeriod > 0,
      }
    }
  );

  const { data: scheduleResponse, isFetching: isLoadingSchedule } = useGenerateLoanScheduleFromConfig(
    { 
      glNo: selectedProductGlNo || '',
      amount: loanAmount,
      period: loanPeriod
    },
    {
      query: {
        enabled: !!selectedProductGlNo && loanAmount > 0 && loanPeriod > 0,
      }
    }
  );

  const feeCalculation = useMemo(() => feeCalculationResponse, [feeCalculationResponse]);
  const scheduleData = useMemo(() => scheduleResponse, [scheduleResponse]);

  const calculatedInstallment = useMemo(() => {
    if (scheduleData?.scheduleDTO && scheduleData.scheduleDTO.length > 0) {
      return scheduleData.scheduleDTO[0].totalDue || 0;
    }
    return 0;
  }, [scheduleData]);

  const loanImpactAnalysis = useMemo((): LoanImpactAnalysis | null => {
    if (!grossSalary || !netSalary || !calculatedInstallment) return null;

    const totalCurrentDeductions = deductions.reduce((sum, d) => sum + d.amount, 0);
    const disbursementFees = feeCalculation?.totalDisbursementFees || 0;
    const takeHomeAmount = (feeCalculation?.netDisbursementAmount || loanAmount) - disbursementFees;
    
    const newLoanInstallment = calculatedInstallment;
    const totalNewDeductions = totalCurrentDeductions + newLoanInstallment;
    const newNetSalary = netSalary - newLoanInstallment;
    const netGrossRatio = newNetSalary / grossSalary;
    const oneThirdThreshold = 1 / 3;
    
    const isHealthy = netGrossRatio >= oneThirdThreshold;
    const warningMessage = !isHealthy 
      ? 'Your remaining net salary will fall below one-third of your gross salary after this loan.'
      : undefined;

    return {
      currentNetSalary: netSalary,
      totalCurrentDeductions,
      newLoanInstallment,
      totalNewDeductions,
      newNetSalary,
      netGrossRatio,
      takeHomeAmount,
      disbursementFees,
      isHealthy,
      warningMessage
    };
  }, [grossSalary, netSalary, calculatedInstallment, deductions, feeCalculation, loanAmount]);

  const handleAddDeduction = useCallback(() => {
    if (!newDeductionName || newDeductionAmount <= 0) {
      message.warning('Please enter deduction name and amount');
      return;
    }

    const newDeduction: Deduction = {
      id: Date.now().toString(),
      name: newDeductionName,
      amount: newDeductionAmount,
      isEditable: true
    };

    setDeductions([...deductions, newDeduction]);
    setNewDeductionName('');
    setNewDeductionAmount(0);
    message.success('Deduction added successfully');
  }, [newDeductionName, newDeductionAmount, deductions]);

  const handleRemoveDeduction = useCallback((id: string) => {
    setDeductions(deductions.filter(d => d.id !== id));
    message.success('Deduction removed');
  }, [deductions]);

  const handleExportPDF = useCallback(() => {
    if (!loanImpactAnalysis || !selectedProduct) {
      message.warning('Please complete all calculations first');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(18);
    doc.setTextColor(0, 132, 209);
    doc.text('Loan Impact Analysis Report', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, pageWidth / 2, 28, { align: 'center' });
    doc.text(`Member: ${fullname || 'N/A'}`, pageWidth / 2, 34, { align: 'center' });
    
    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 132, 209);
    doc.line(20, 38, pageWidth - 20, 38);

    let yPos = 48;

    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('Loan Details', 20, yPos);
    yPos += 8;

    const loanDetails = [
      ['Product', selectedProduct.productName || 'N/A'],
      ['Loan Amount', `TZS ${loanAmount.toLocaleString()}`],
      ['Loan Period', `${loanPeriod} months`],
      ['Monthly Installment', `TZS ${calculatedInstallment.toLocaleString()}`],
      ['Disbursement Fees', `TZS ${loanImpactAnalysis.disbursementFees.toLocaleString()}`],
      ['Net Amount to Receive', `TZS ${loanImpactAnalysis.takeHomeAmount.toLocaleString()}`]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: loanDetails,
      theme: 'grid',
      styles: { fontSize: 10 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' }
      }
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    doc.setFontSize(14);
    doc.text('Salary Information', 20, yPos);
    yPos += 8;

    const salaryInfo = [
      ['Gross Salary', `TZS ${grossSalary.toLocaleString()}`],
      ['Current Net Salary', `TZS ${netSalary.toLocaleString()}`],
      ['New Net Salary (After Loan)', `TZS ${loanImpactAnalysis.newNetSalary.toLocaleString()}`],
      ['Net/Gross Ratio', `${(loanImpactAnalysis.netGrossRatio * 100).toFixed(2)}%`]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: salaryInfo,
      theme: 'grid',
      styles: { fontSize: 10 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' }
      }
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    if (deductions.length > 0) {
      doc.setFontSize(14);
      doc.text('Current Deductions', 20, yPos);
      yPos += 8;

      const deductionsData = deductions.map(d => [d.name, `TZS ${d.amount.toLocaleString()}`]);
      deductionsData.push(['Total Current Deductions', `TZS ${loanImpactAnalysis.totalCurrentDeductions.toLocaleString()}`]);

      autoTable(doc, {
        startY: yPos,
        head: [],
        body: deductionsData,
        theme: 'grid',
        styles: { fontSize: 10 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 80 },
          1: { cellWidth: 'auto' }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    doc.setFontSize(14);
    doc.text('Impact Summary', 20, yPos);
    yPos += 8;

    const impactSummary = [
      ['Total Monthly Deductions (Before)', `TZS ${loanImpactAnalysis.totalCurrentDeductions.toLocaleString()}`],
      ['New Loan Installment', `TZS ${loanImpactAnalysis.newLoanInstallment.toLocaleString()}`],
      ['Total Monthly Deductions (After)', `TZS ${loanImpactAnalysis.totalNewDeductions.toLocaleString()}`],
      ['Financial Health Status', loanImpactAnalysis.isHealthy ? 'HEALTHY ✓' : 'WARNING ⚠']
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: impactSummary,
      theme: 'grid',
      styles: { fontSize: 10 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' }
      }
    });

    if (loanImpactAnalysis.warningMessage) {
      yPos = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.setTextColor(200, 0, 0);
      doc.text(`⚠ ${loanImpactAnalysis.warningMessage}`, 20, yPos, { maxWidth: pageWidth - 40 });
    }

    yPos = doc.internal.pageSize.getHeight() - 20;
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('This is an estimate and subject to final approval. Actual terms may vary.', pageWidth / 2, yPos, { align: 'center' });

    doc.save(`Loan_Impact_Analysis_${new Date().getTime()}.pdf`);
    message.success('PDF downloaded successfully');
  }, [loanImpactAnalysis, selectedProduct, loanAmount, loanPeriod, calculatedInstallment, grossSalary, netSalary, deductions, fullname]);

  return (
    <Card
      className={`shadow-lg h-full ${dark ? 'bg-gray-800' : 'bg-white'} ${className || ''}`}
      bodyStyle={{ padding: '24px' }}
    >
      <div className="flex items-center justify-between mb-4">
        <Title level={4} className={`m-0 ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
          <CalculatorOutlined className="mr-2" />
          Loan Calculator
        </Title>
        <Button
          type={isExpanded ? 'default' : 'primary'}
          onClick={() => setIsExpanded(!isExpanded)}
          icon={isExpanded ? <CaretRightOutlined rotate={90} /> : <CaretRightOutlined />}
        >
          {isExpanded ? 'Hide Calculator' : 'Show Calculator'}
        </Button>
      </div>

      {!isExpanded && (
        <div style={{ minHeight: collapsedPlaceholderMinHeight }} className="flex items-center">
          <Alert
            message="Click 'Show Calculator' to estimate your loan impact and affordability"
            type="info"
            showIcon
            icon={<CalculatorOutlined />}
            className="mb-0 w-full"
          />
        </div>
      )}

      {isExpanded && (
        <Form form={form} layout="vertical">
        <Collapse 
          defaultActiveKey={['loan', 'salary']} 
          ghost
          expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        >
          <Collapse.Panel header="Loan Information" key="loan">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Loan Product" required>
                  <Select
                    placeholder="Select loan product"
                    value={selectedProductGlNo}
                    onChange={setSelectedProductGlNo}
                    loading={isProductsLoading}
                  >
                    {products?.map(product => (
                      <Option key={product.glNo} value={product.glNo}>
                        {product.productName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Loan Amount (TZS)" required>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    value={loanAmount}
                    onChange={(val) => setLoanAmount(val || 0)}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // @ts-ignore
                    parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Loan Period (Months)" required>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    max={60}
                    value={loanPeriod}
                    onChange={(val) => setLoanPeriod(val || 12)}
                  />
                </Form.Item>
              </Col>
            </Row>

            {(isLoadingSchedule || isLoadingFees) && (
              <div className="text-center py-4">
                <Spin tip="Calculating..." />
              </div>
            )}

            {calculatedInstallment > 0 && (
              <Alert
                message={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong>Monthly Installment: TZS {calculatedInstallment.toLocaleString()}</Text>
                    {feeCalculation && (
                      <>
                        <Text>Disbursement Fees: TZS {feeCalculation.totalDisbursementFees?.toLocaleString() || 0}</Text>
                        <Text>Net Amount to Receive: TZS {feeCalculation.netDisbursementAmount?.toLocaleString() || 0}</Text>
                      </>
                    )}
                  </Space>
                }
                type="info"
                showIcon
                icon={<DollarOutlined />}
              />
            )}
          </Collapse.Panel>

          <Collapse.Panel header="Salary & Deductions" key="salary">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Gross Salary (TZS)" required>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    value={grossSalary}
                    onChange={(val) => setGrossSalary(val || 0)}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // @ts-ignore
                    parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Net Salary (TZS)" required>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    value={netSalary}
                    onChange={(val) => setNetSalary(val || 0)}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // @ts-ignore
                    parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider>Current Deductions</Divider>

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Row gutter={8}>
                <Col xs={24} sm={10}>
                  <Input
                    placeholder="Deduction name"
                    value={newDeductionName}
                    onChange={e => setNewDeductionName(e.target.value)}
                  />
                </Col>
                <Col xs={24} sm={10}>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Amount"
                    min={0}
                    value={newDeductionAmount}
                    onChange={val => setNewDeductionAmount(val || 0)}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // @ts-ignore
                    parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                  />
                </Col>
                <Col xs={24} sm={4}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddDeduction}
                    block
                  >
                    Add
                  </Button>
                </Col>
              </Row>

              {deductions.length > 0 && (
                <List
                  size="small"
                  dataSource={deductions}
                  renderItem={item => (
                    <List.Item
                      actions={[
                        <Popconfirm
                          title="Remove this deduction?"
                          onConfirm={() => handleRemoveDeduction(item.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      ]}
                    >
                      <List.Item.Meta
                        title={item.name}
                        description={`TZS ${item.amount.toLocaleString()}`}
                      />
                    </List.Item>
                  )}
                />
              )}
            </Space>
          </Collapse.Panel>
        </Collapse>

        {loanImpactAnalysis && (
          <>
            <Divider>Loan Impact Analysis</Divider>

            <Alert
              message={
                <Space direction="horizontal" size="middle">
                  {loanImpactAnalysis.isHealthy ? (
                    <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                  ) : (
                    <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />
                  )}
                  <Text strong>
                    {loanImpactAnalysis.isHealthy ? 'Healthy Financial Position' : 'Financial Warning'}
                  </Text>
                </Space>
              }
              description={loanImpactAnalysis.warningMessage}
              type={loanImpactAnalysis.isHealthy ? 'success' : 'warning'}
              showIcon={false}
              className="mb-4"
            />

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="Current Net Salary"
                  value={loanImpactAnalysis.currentNetSalary}
                  precision={0}
                  prefix="TZS"
                  valueStyle={{ color: dark ? '#fff' : '#000' }}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="New Net Salary"
                  value={loanImpactAnalysis.newNetSalary}
                  precision={0}
                  prefix="TZS"
                  valueStyle={{ 
                    color: loanImpactAnalysis.isHealthy 
                      ? '#52c41a' 
                      : '#faad14' 
                  }}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="Net/Gross Ratio"
                  value={loanImpactAnalysis.netGrossRatio * 100}
                  precision={2}
                  suffix="%"
                  valueStyle={{ 
                    color: loanImpactAnalysis.isHealthy 
                      ? '#52c41a' 
                      : '#faad14' 
                  }}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="Current Deductions"
                  value={loanImpactAnalysis.totalCurrentDeductions}
                  precision={0}
                  prefix="TZS"
                  valueStyle={{ color: dark ? '#fff' : '#000' }}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="New Loan Installment"
                  value={loanImpactAnalysis.newLoanInstallment}
                  precision={0}
                  prefix="TZS"
                  valueStyle={{ color: '#0084d1' }}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="Total New Deductions"
                  value={loanImpactAnalysis.totalNewDeductions}
                  precision={0}
                  prefix="TZS"
                  valueStyle={{ color: dark ? '#fff' : '#000' }}
                />
              </Col>
            </Row>

            <div className="mt-6 text-center">
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleExportPDF}
              >
                Download Impact Analysis (PDF)
              </Button>
            </div>
          </>
        )}
        </Form>
      )}
    </Card>
  );
};
